import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { csModal, Modal } from '@cargos/cs-modal';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { ProfileRoute } from './profile-route';
import { CurrentUserProfileService } from './shared';
import { AcceptanceLegalTermsService } from './shared/modules/acceptance-legal-terms/services/acceptance-legal-terms.service';
import { NetworkConnectionService } from './shared/modules/network-connection/network-connection.service';
import { VersionService } from './shared/modules/version-update/services/version.service';
import { RouteMemoryService } from './shared/services/route-memory.service';
import { AuthService, CurrentUserService, FaviconService, MessagingService } from './utils';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})

export class AppComponent implements OnInit, OnDestroy, AfterViewInit {

    title = 'app';
    public settingsModal: Observable<Modal>;
    private unsubscribe$: Subject<void>;

    constructor(
        private authService: AuthService,
        private networkService: NetworkConnectionService,
        private faviconService: FaviconService,
        private messagingService: MessagingService,
        private currentUserProfileService: CurrentUserProfileService,
        private router: Router,
        private currentUserService: CurrentUserService,
        private acceptanceLegalTermsService: AcceptanceLegalTermsService,
        private routeMemoryService: RouteMemoryService,
        private versionService: VersionService
    ) {
        this.settingsModal = csModal.getSettings();
        this.unsubscribe$ = new Subject<void>();
    }

    get settingsAcceptanceLegalModal(): Observable<Modal> {
        return this.acceptanceLegalTermsService.getSettings();
    }

    ngOnInit(): void {
        this.authService.getUser();
        this.listenConnectionChanges();
        this.routeMemoryService.init();
        this.listenVersionUpdates();
    }

    ngAfterViewInit(): void {
        this.subscribeUserProfileChangesThroughTaps();
        this.subscribeUserProfile();
        this.currentUserProfileService.initListener();
        this.subscribeFavicon();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    private listenVersionUpdates(): void {
        this.versionService.startListening()
            .pipe(
                takeUntil(this.unsubscribe$)
            )
            .subscribe();
    }

    subscribeUserProfile(): void {
        this.currentUserService.getProfile()
            .pipe(
                takeUntil(this.unsubscribe$)
            )
            .subscribe(usr_profile => {
                if (usr_profile && this.messagingService.isUserToNotify(usr_profile)) {
                    this.messagingService.init();
                }
            });
    }

    listenConnectionChanges(): void {
        window.addEventListener('online', this.handleConnectionChange);
        window.addEventListener('offline', this.handleConnectionChange);
    }

    handleConnectionChange = (event): void => {
        if (event.type == 'offline') {
            this.networkService.sendMessage({
                iconClass: 'offline',
                description: 'Your device lost its internet connection.',
                isConnectedClass: 'active',
                isConnected: false
            });
        }
        if (event.type == 'online') {
            this.networkService.sendMessage({
                iconClass: 'online',
                description: 'Your device is connected to the internet.',
                isConnectedClass: '',
                isConnected: true
            });
        }
    }

    subscribeUserProfileChangesThroughTaps(): void {
        const urls = Object.values(ProfileRoute)
            .map(commands => `/${commands.join('/')}`);
        urls.push('/session-expired');

        this.currentUserProfileService.onUserChangesThroughTaps()
            .pipe(
                switchMap(() =>
                    this.authService.onUserChanges()
                        .pipe(
                            take(1)
                        )
                ),
                takeUntil(this.unsubscribe$)
            )
            .subscribe(() => {
                const currentUrl = this.router.url;
                const reload = urls.some(url => currentUrl.startsWith(url));

                if (reload) {
                    this.router.navigateByUrl(currentUrl);
                }
            });
    }

    getOrganizationFavicon(): Observable<string> {
        return this.currentUserService.getOrganization()
            .pipe(
                map(organization => organization && organization.org_cargo_favicon || ''),
                distinctUntilChanged()
            );
    }

    subscribeFavicon(): void {
        this.getOrganizationFavicon()
            .pipe(
                takeUntil(this.unsubscribe$)
            )
            .subscribe(favicon => {
                if (favicon) {
                    this.faviconService.setIcon({
                        href: favicon
                    });
                } else {
                    this.faviconService.resetIcon();
                }
            });
    }

}
